/**
 * Sends the user to the login page, returning them to the *current page* once they re-authenticate.
 *
 * We deliberately use the current browser location for `next` rather than the URL of the request that failed: an
 * expired session is detected via a (potentially `/api/...`) XHR, and sending the user back to an API URL after login
 * is useless. Reloading the current page instead would loop, since the same request would fail again.
 */
const redirectToLogin = () => {
  const { pathname, search } = window.location;
  // Already on an AuthN page - don't bounce again (avoids a redirect loop).
  if (pathname.startsWith("/authn")) {
    return;
  }
  // Mirror the backend's staff/customer split (see core.routing.get_base_login_url).
  const loginPath = pathname.startsWith("/staff") ? "/authn/login-staff" : "/authn/login";
  const next = encodeURIComponent(pathname + search);
  window.location.assign(`${loginPath}?next=${next}`);
};

/**
 * Handles authentication failures by detecting either:
 * 1. Server redirects to login pages (HTML responses to API calls)
 * 2. Standard 401 Unauthorized responses
 *
 * In both cases, sends the user to the login page (see `redirectToLogin`).
 *
 * @param response - The axios response object
 * @throws Error if server redirected to login page
 */
export const handleAuthenticationFailure = (response: any) => {
  // Check if server redirected API request to login page (HTML response)
  const isHtmlResponse = response?.headers?.["content-type"]?.includes?.("text/html");

  if (isHtmlResponse) {
    const { pathname } = response.request?.responseURL ? new URL(response.request.responseURL) : { pathname: "" };

    if (pathname.startsWith("/authn/")) {
      redirectToLogin();
      throw new Error("Server redirected API request to login page, most likely due to expired session");
    }
  }

  // Handle standard 401 Unauthorized responses
  if (response?.status === 401) {
    redirectToLogin();
  }
};
